import type { JobAdClient } from '../types/jobAd';
import { jamFormStepApplicationServiceSetup } from './useJamFormStepApplication';
import { jamFormStepContentServiceSetup } from './useJamFormStepContent';
import { jamFormStepDataServiceSetup } from './useJamFormStepData';
import { jamFormStepDesignServiceSetup } from './useJamFormStepDesign';

const duplicatedJob = ref<JobAdClient | null>(null);

export const useDuplicateJob = () => {
    const DUPLICATED_JOB_KEY = 'duplicatedJob';

    type DuplicationSteps = 'data' | 'content' | 'application' | 'design';

    type DuplicationObject = {
        completedSteps: Array<DuplicationSteps>,
        duplicationId: string|null,
        job: JobAdClient
    }

    const setDuplicatedJob = (job: JobAdClient | null) => {
        duplicatedJob.value = job;
        localStorage.setItem(DUPLICATED_JOB_KEY, JSON.stringify({
            completedSteps: [],
            duplicationId: null,
            job: job
        }));
    };

    if (duplicatedJob.value === null && process.client) {
        const storedJob = localStorage?.getItem(DUPLICATED_JOB_KEY);
        let parsedJob = null;
        if (storedJob) parsedJob = JSON.parse(storedJob);
        if (
            !parsedJob ||
            !parsedJob.duplicationId ||
            parsedJob.duplicationId !== useRoute().params.id
        ) {
            if (parsedJob && !parsedJob.duplicationId) setDuplicatedJob(null);
            duplicatedJob.value = null;
        } else {
            duplicatedJob.value = parsedJob.job;
        }
    }

    const setDuplicatedJobId = (jobId: string) => {
        const duplicationObject = getDuplicationObject();
        if (duplicationObject !== null) {
            duplicationObject.duplicationId = jobId;
            localStorage.setItem(DUPLICATED_JOB_KEY, JSON.stringify(duplicationObject));
        }
    };

    const setCompletedDuplicatedJob = (step: 'data' | 'content' | 'application' | 'design') => {
        const duplicationObject = getDuplicationObject();
        if (duplicationObject !== null && !isDuplicateStepCompleted(step)) {
            duplicationObject.completedSteps.push(step);
            localStorage.setItem(DUPLICATED_JOB_KEY, JSON.stringify(duplicationObject));
        }
    }

    const isDuplicateStepCompleted = (step: DuplicationSteps): boolean => {
        return getDuplicationObject()?.completedSteps.includes(step) ?? false;
    }

    const existsDuplicateJob = () => {
        return duplicatedJob.value !== null;
    };

    const getDuplicationObject = (): DuplicationObject|null => {
        const storageValue = localStorage?.getItem(DUPLICATED_JOB_KEY);
        if (storageValue === null)  {
            return null;
        }
        return JSON.parse(storageValue);
    }

    const getDuplicatedJobFormStepData = () => {
        if (duplicatedJob.value === null || isDuplicateStepCompleted('data')) return null;
        return jamFormStepDataServiceSetup.outbound(duplicatedJob.value);
    };

    const getDuplicatedJobFormStepContent = () => {
        if (duplicatedJob.value === null || isDuplicateStepCompleted('content')) return null;
        return jamFormStepContentServiceSetup.outbound(duplicatedJob.value);
    };

    const getDuplicatedJobFormStepApplication = () => {
        if (duplicatedJob.value === null || isDuplicateStepCompleted('application')) return null;
        return jamFormStepApplicationServiceSetup.outbound(duplicatedJob.value);
    };

    const getDuplicatedJobFormStepDesign = () => {
        if (duplicatedJob.value === null || isDuplicateStepCompleted('design')) return null;
        return jamFormStepDesignServiceSetup.outbound(duplicatedJob.value);
    };

    return {
        existsDuplicateJob,
        getDuplicatedJobFormStepApplication,
        getDuplicatedJobFormStepContent,
        getDuplicatedJobFormStepData,
        getDuplicatedJobFormStepDesign,
        setCompletedDuplicatedJob,
        setDuplicatedJob,
        setDuplicatedJobId,
    };
};
